import { useManager } from 'dreamstate';
import { useCallback, useMemo, useState } from 'react';

import { EllipsisV } from '@vimeo/bokeh-icons';
import { Definable, Id, Optional } from '@vimeo/core/types/generic';
import { ChatSettingsButton } from '@vimeo/live-core/components/interaction/chat/ChatSettingsButton';
import { PollSettingsButton } from '@vimeo/live-core/components/interaction/polls/PollSettingsButton';
import { QnaSettingsButton } from '@vimeo/live-core/components/interaction/qna/QnaSettingsButton';
import { PanelChatIcon } from '@vimeo/live-core/components/interaction/shared/panel_icons/PanelChatIcon';
import { PanelPollIcon } from '@vimeo/live-core/components/interaction/shared/panel_icons/PanelPollIcon';
import { PanelQnaIcon } from '@vimeo/live-core/components/interaction/shared/panel_icons/PanelQnaIcon';
import { useLiveGlobals } from '@vimeo/live-core/components/providers/globals';
import { ChatManager } from '@vimeo/live-core/store/firebase/interaction/chat';
import { PollManager } from '@vimeo/live-core/store/firebase/interaction/poll';
import { QnAManager } from '@vimeo/live-core/store/firebase/interaction/qna';
import { InteractionSessionManager } from '@vimeo/live-core/store/interaction/session';
import { T_CHAT, T_POLLS, T_QNA } from '@vimeo/live-core/translations';
import {
  EChatType,
  EInteractionModule,
  IInteractionToolsItemDescriptor,
} from '@vimeo/live-core/types/interaction_tools';
import { IInteractionStoreInitialState } from '@vimeo/live-core/types/store';

import { ChatManage } from '../../chat/ChatManage';
import { ChatView } from '../../chat/ChatView';
import { PollManage } from '../../polls/manage/PollManage';
import { PollView } from '../../polls/view/PollView';
import { QnaManage } from '../../qna/QnaManage';
import { QnaView } from '../../qna/QnaView';

export interface IInteractionSidebarItems {
  items: Array<IInteractionToolsItemDescriptor>;
  addItem: (item: IInteractionToolsItemDescriptor) => void;
  removeItem: (itemId: EInteractionModule | Id) => void;
}

export function useInteractionSidebarItems(
  activeModule: Definable<EInteractionModule> | Optional<Id>,
): IInteractionSidebarItems {
  const {
    initialState: { interaction },
  } = useLiveGlobals<IInteractionStoreInitialState>();
  const {
    roomUser: { value: roomUser },
  } = useManager(InteractionSessionManager);
  const { isEnabled } = useManager(ChatManager, ({ isEnabled }) => [isEnabled]);
  const { polls, activePollId } = useManager(PollManager, ({ polls, activePollId }) => [
    polls,
    activePollId,
  ]);
  const { activeSessionId } = useManager(QnAManager, ({ activeSessionId }) => [activeSessionId]);

  const isManagementAccessed: boolean = Boolean(
    roomUser?.isCreator && !interaction?.feature?.isManagementDisabled,
  );
  // Check the active module here to keep tool content visible if the tool becomes inactive.
  // In this way, the user will see a tool disabled state instead of the empty content panel.
  const isChatVisible: boolean =
    activeModule === EInteractionModule.CHAT || isManagementAccessed || Boolean(isEnabled);
  const isPollsVisible: boolean =
    activeModule === EInteractionModule.POLL ||
    isManagementAccessed ||
    Boolean(activePollId && polls[activePollId]);
  const isQnaVisible: boolean =
    activeModule === EInteractionModule.QNA || isManagementAccessed || Boolean(activeSessionId);
  const defaultItems: Array<IInteractionToolsItemDescriptor> = useMemo(() => {
    const chatItem: IInteractionToolsItemDescriptor = {
      id: EInteractionModule.CHAT,
      label: T_CHAT,
      icon: (
        <PanelChatIcon
          isActive={activeModule === EInteractionModule.CHAT}
          isManagementAccessed={isManagementAccessed}
        />
      ),
      header: T_CHAT,
      headerMenu: isManagementAccessed ? (
        <ChatSettingsButton buttonIcon={<EllipsisV />} />
      ) : undefined,
      Content: isManagementAccessed
        ? ChatManage
        : ({ id }: { id: string }) => <ChatView id={id} chatType={EChatType.PUBLIC} />,
    };

    const pollItem: IInteractionToolsItemDescriptor = {
      id: EInteractionModule.POLL,
      label: T_POLLS,
      icon: <PanelPollIcon isActive={activeModule === EInteractionModule.POLL} />,
      header: T_POLLS,
      headerMenu: isManagementAccessed ? (
        <PollSettingsButton buttonIcon={<EllipsisV />} />
      ) : undefined,
      Content: isManagementAccessed ? PollManage : PollView,
    };

    const qnaItem: IInteractionToolsItemDescriptor = {
      id: EInteractionModule.QNA,
      label: T_QNA,
      icon: (
        <PanelQnaIcon
          isActive={activeModule === EInteractionModule.QNA}
          isManagementAccessed={isManagementAccessed}
        />
      ),
      header: T_QNA,
      headerMenu: isManagementAccessed ? (
        <QnaSettingsButton buttonIcon={<EllipsisV />} />
      ) : undefined,
      Content: isManagementAccessed ? QnaManage : QnaView,
    };

    return ([] as Array<IInteractionToolsItemDescriptor>).concat(
      isChatVisible ? chatItem : [],
      isPollsVisible ? pollItem : [],
      isQnaVisible ? qnaItem : [],
    );
  }, [isManagementAccessed, isChatVisible, isPollsVisible, isQnaVisible, activeModule]);

  // this part for feature marketplace
  const [customItems, setCustomItems] = useState<Array<IInteractionToolsItemDescriptor>>([]);

  const addItem = useCallback((item: IInteractionToolsItemDescriptor) => {
    if (item) {
      setCustomItems((customItems) => [...customItems, item]);
    }
  }, []);

  const removeItem = useCallback((itemId: EInteractionModule | Id) => {
    if (itemId) {
      setCustomItems((customItems) => customItems.filter((it) => it.id !== itemId));
    }
  }, []);

  const items: Array<IInteractionToolsItemDescriptor> = useMemo(
    () => [...defaultItems, ...customItems],
    [defaultItems, customItems],
  );

  return { items, addItem, removeItem };
}
